import { Slot } from "@radix-ui/react-slot";
import { cva, VariantProps } from "class-variance-authority";
import React from "react";
import { cn } from "../utils/cn";

const buttonVariants = cva(
  "inline-flex text-base items-center justify-center whitespace-nowrap rounded-xl overflow-hidden font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-slate-800 text-slate-50 hover:bg-slate-800/90",
        destructive: "bg-red-500 text-slate-50 hover:bg-red-500/90",
        outline:
          "border border-slate-200 bg-white hover:bg-slate-100 hover:text-slate-900",
        secondary: "bg-slate-100 text-slate-900 hover:bg-slate-100/80",
        ghost:
          "hover:bg-slate-100 hover:text-slate-900 border border-cyan-700/50",
        link: "text-slate-900 underline-offset-4 underline font-normal hover:opacity-80",
      },

      color: {
        default: "",
        orange: "bg-orange-300 text-slate-800 hover:bg-orange-300/90",
      },
      size: {
        default: "h-12 px-12 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
    compoundVariants: [
      {
        variant: "link",
        size: "default",
        className: "h-auto py-0 px-0",
      },
    ],
  }
);

export interface ButtonBaseProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  color?: VariantProps<typeof buttonVariants>["color"];
}

const ButtonBase = React.forwardRef<HTMLButtonElement, ButtonBaseProps>(
  ({ className, variant, size, color, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, color, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
ButtonBase.displayName = "Button";

const GhostButton = (props: ButtonBaseProps) => (
  <ButtonBase variant="ghost" {...props} />
);

const LinkButton = (props: ButtonBaseProps) => (
  <ButtonBase variant="link" {...props} />
);

export { ButtonBase as Button, buttonVariants, GhostButton, LinkButton };
